import { useEffect, useState } from 'react';
import { useDesktopBreakpoint } from './useDesktopBreakpoint';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

interface NetworkInformation {
  saveData?: boolean;
  effectiveType?: string;
}

const SLOW = ['slow-2g', '2g', '3g'];

/**
 * Whether to load a purely decorative background video.
 *
 * These clips render at 0.4 opacity behind a 2-4px blur under a white overlay —
 * they are atmosphere, never information — so nobody should pay for one on a
 * phone, a metered connection, or after asking for reduced motion. The pages
 * fall back to their heroImage wash, which is 2-8 KB.
 *
 * Mirrors the checks Home already made inline before its hero video; this
 * generalises them so every decorative video answers to the same rule.
 */
export function useDecorativeVideo(): boolean {
  const isDesktop = useDesktopBreakpoint();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [connectionAllows, setConnectionAllows] = useState(true);

  useEffect(() => {
    const nav = navigator as Navigator & { connection?: NetworkInformation };
    const c = nav.connection;
    if (!c) return;
    const evaluate = () => setConnectionAllows(!c.saveData && !SLOW.includes(c.effectiveType || ''));
    evaluate();
    const target = c as unknown as EventTarget;
    target.addEventListener?.('change', evaluate);
    return () => target.removeEventListener?.('change', evaluate);
  }, []);

  return isDesktop && !prefersReducedMotion && connectionAllows;
}

export default useDecorativeVideo;
